import { ref } from 'vue';
import COS from 'cos-js-sdk-v5';
import { uploadCredentials } from '@/request/customrize';

// 定义上传状态类型
type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

// 定义上传选项类型
interface UploadOptions {
  uploadType: string; // 上传类型，例如 'video/' 或 'image/'
  cosTokenData?: any; // 可选的 COS 凭证数据
  onProgress?: (progressData: any) => void; // 上传进度回调
  onSuccess?: (data: any) => void; // 上传成功回调
  onError?: (err: any) => void; // 上传失败回调
}
// 获取上传凭证
export const getUploadCredentials = async () => {
  try {
    const res = (await uploadCredentials()) as any;
    let { code, data } = res.data;
    if (code === 200) {
      return data; // 返回获取到的凭证数据
    }
  } catch (error) {
    console.error('获取上传凭证时出错', error);
  }
  return null; // 如果获取失败，返回 null
};

export function useCosUpload() {
  // 存储 COS 凭证数据
  const cosTokenData = ref(null);
  // 存储上传状态
  const uploadStatus = ref<UploadStatus>('idle');

  // 从路径前缀中提取 bucket 和 region 信息
  const getBucketAndRegion = (pathPrefix: string) => {
    if (pathPrefix.startsWith('https://')) pathPrefix = pathPrefix.substring(8);
    const domainIndexOf = pathPrefix.indexOf('myqcloud.com');
    const prefixPart = pathPrefix.substring(0, domainIndexOf);
    const split = prefixPart.split('.');
    return { bucket: split[0], regionName: split[2] };
  };

  // 生成上传文件的 key
  const getKey = (pathPrefix: string, uploadType: string, fileName: string) => {
    const domainIndexOf = pathPrefix.indexOf('myqcloud.com');
    return pathPrefix.substring(domainIndexOf + 13) + uploadType + fileName;
  };

  // 上传文件前的准备工作
  const beforeUpload = async (file: File, options: UploadOptions) => {
    const { uploadType, onProgress, onSuccess, onError } = options;
    let { cosTokenData } = options;

    // 如果没有传入 cosTokenData，则获取上传凭证
    if (!cosTokenData) {
      cosTokenData = await getUploadCredentials();
      if (!cosTokenData) {
        uploadStatus.value = 'error'; // 更新状态为错误
        if (onError) onError(new Error('获取上传凭证失败'));
        return;
      }
    }

    // 初始化 COS 实例
    const cos = new COS({
      getAuthorization: (options, callback) => {
        callback({
          TmpSecretId: cosTokenData.credentials.tmpSecretId,
          TmpSecretKey: cosTokenData.credentials.tmpSecretKey,
          XCosSecurityToken: cosTokenData.credentials.token,
          ExpiredTime: cosTokenData.expiredTime,
          StartTime: cosTokenData?.startTime || Math.floor(Date.now() / 1000), // 添加 StartTime 属性
        });
      },
    });

    // 获取 bucket 和 region 信息
    const { bucket, regionName } = getBucketAndRegion(cosTokenData.pathPrefix);
    // 生成文件的 key
    const key = getKey(cosTokenData.pathPrefix, uploadType, file.name);

    // 更新上传状态为上传中
    uploadStatus.value = 'uploading';

    // 上传文件
    cos.putObject(
      {
        Bucket: bucket,
        Region: regionName,
        Key: key,
        Body: file,
        onProgress: (progressData) => {
          if (onProgress) onProgress(progressData); // 调用上传进度回调
        },
      },
      (err, data) => {
        if (err) {
          uploadStatus.value = 'error'; // 更新状态为错误
          if (onError) onError(err); // 调用上传失败回调
        } else {
          uploadStatus.value = 'success'; // 更新状态为成功
          if (onSuccess) onSuccess(data); // 调用上传成功回调
        }
      }
    );
  };

  return {
    beforeUpload, // 返回上传前的准备方法
    uploadStatus, // 返回上传状态
  };
}
